/**
 * Quem pode apagar o que, no chat do AppCel — a regra pura ([DA-574]).
 *
 * Vive aqui, em `@fiodecobre/shared`, porque as DUAS pontas precisam da mesma
 * resposta e por motivos diferentes: a **tela** decide se oferece o botao "apagar
 * para todos", e o **servidor** recusa de verdade. Duas copias divergiriam no
 * primeiro ajuste — e a que some e' sempre a do backend, que e' a que protege.
 *
 * ## As tres regras (as do WhatsApp, e a diferenca importa)
 *
 * | A mensagem e' | Dentro da janela | Depois |
 * |---|---|---|
 * | **minha** (eu enviei) | escolho: so pra mim **ou** para todos | so pra mim |
 * | **recebida** | so pra mim | so pra mim |
 *
 * ⛔ **Mensagem RECEBIDA nunca sai para todos, em janela nenhuma.** Nao e' detalhe de
 * copia: sem isso o cliente apagaria da inbox da loja o que a vendedora escreveu — o
 * preco que ela passou, o prazo que prometeu —, e some do lado dela tambem. Neste
 * chat circula orcamento e extrato; e' registro comercial. A [DA-541] ja tinha
 * travado o mesmo no sentido loja→cliente; aqui a regra fecha os dois sentidos.
 *
 * ## Lote e' tudo-ou-nada, de proposito
 *
 * Selecao mista (uma minha e recente, uma recebida) **nao** apaga pra todos as que
 * dao e esconde as outras: um clique faria duas coisas diferentes e a folha de
 * confirmacao nao conseguiria dizer a verdade numa frase. Se qualquer mensagem da
 * selecao nao puder ir pra todos, so sobra o "apagar so pra mim" — que e' o que o
 * WhatsApp faz, e portanto o que ele ja espera.
 */

/**
 * Janela do "apagar para todos", em minutos.
 *
 * ⚠️ **1 hora e' escolha DELE (18/08/2026), nao o numero do WhatsApp.** Ele propos 5
 * minutos; a recomendacao aceita foi 1 hora, porque o caso real e' *"mandei a foto
 * errada"* e isso quase nunca se percebe em 5 minutos — passado o prazo, a foto errada
 * fica na tela da loja pra sempre. Janela menor protege mais o registro; maior perdoa
 * mais engano. Trocar aqui muda a tela e o servidor de uma vez.
 */
export const JANELA_APAGAR_PARA_TODOS_MIN = 60;

export interface MensagemApagavel {
  /** `true` quando fui EU que enviei (no app do cliente, a `direcao` inbound). */
  minha: boolean;
  /** Quando a mensagem foi enviada. */
  criadoEm: Date | string;
}

/** Minutos entre o envio e `agora`. Data invalida vira `Infinity` — fora da janela. */
function minutosDesde(criadoEm: Date | string, agora: Date): number {
  const d = criadoEm instanceof Date ? criadoEm : new Date(criadoEm);
  const t = d.getTime();
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return (agora.getTime() - t) / 60000;
}

/**
 * Esta mensagem, sozinha, pode ser apagada para todos?
 *
 * ⚠️ **Falha FECHADA**: data ilegivel responde `false`. O desfecho ruim de um `false`
 * errado e' a pessoa nao conseguir apagar dos dois lados; o de um `true` errado e'
 * uma mensagem sumir da tela de quem nao pediu. Os dois erros nao custam igual.
 */
export function podeApagarParaTodos(m: MensagemApagavel, agora: Date = new Date()): boolean {
  if (!m.minha) return false;
  return minutosDesde(m.criadoEm, agora) <= JANELA_APAGAR_PARA_TODOS_MIN;
}

/** Motivo pelo qual o lote NAO pode ir para todos — `null` quando pode. */
export type MotivoSoParaMim = 'recebida' | 'antiga';

/**
 * O que este LOTE aceita.
 *
 * `motivo` existe pra tela poder dizer **por que** so ha uma saida, em vez de mostrar
 * um botao apagado e mudo. Quando as duas razoes aparecem no mesmo lote, a `recebida`
 * vence: ela nao tem conserto (nenhuma espera a torna apagavel pra todos), enquanto
 * "antiga" ao menos explica um prazo que existiu.
 */
export function decidirApagarLote(
  msgs: MensagemApagavel[],
  agora: Date = new Date(),
): { paraTodos: boolean; motivo: MotivoSoParaMim | null } {
  // Lote vazio nao autoriza nada — nao ha o que apagar, e responder `true` deixaria a
  // tela oferecer um botao que agiria sobre coisa nenhuma.
  if (msgs.length === 0) return { paraTodos: false, motivo: null };
  if (msgs.some((m) => !m.minha)) return { paraTodos: false, motivo: 'recebida' };
  if (msgs.some((m) => !podeApagarParaTodos(m, agora))) return { paraTodos: false, motivo: 'antiga' };
  return { paraTodos: true, motivo: null };
}
